import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import documentService, { UploadProgress, ProcessingConfig } from '../../services/documentService';
import promptService, { Prompt, HarmonySettings } from '../../services/promptService';

interface DocumentUploadProps {
  onUploadComplete?: () => void;
  maxFileSize?: number; // in bytes
  acceptedFormats?: string[]; // MIME types
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onUploadComplete,
  maxFileSize = 104857600, // 100MB default
  acceptedFormats,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [symphonySettings, setSymphonySettings] = useState<HarmonySettings | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load symphony settings and prompts
      const [settings, fetchedPrompts] = await Promise.all([
        promptService.getSymphonySettings(),
        promptService.getSymphonyPrompts()
      ]);
      setSymphonySettings(settings);
      setPrompts(fetchedPrompts);
    } catch (err) {
      console.error('Failed to load symphony settings:', err);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const validateFile = (file: File): string | null => {
    if (maxFileSize && file.size > maxFileSize) {
      return `File "${file.name}" exceeds maximum size of ${documentService.formatFileSize(maxFileSize)}`;
    }
    if (acceptedFormats && acceptedFormats.length > 0 && !acceptedFormats.includes(file.type)) {
      return `File "${file.name}" has unsupported format`;
    }
    return null;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles: File[] = [];
    const errors: string[] = [];

    droppedFiles.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, [maxFileSize, acceptedFormats]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);
    const validFiles: File[] = [];
    const errors: string[] = [];

    selectedFiles.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select files to upload');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress([]);

    try {
      // Build processing config from symphony settings
      let processingConfig: ProcessingConfig | undefined;
      if (symphonySettings) {
        processingConfig = {
          prompt_id: symphonySettings.prompt_id,
          chunk_size: symphonySettings.chunk_size,
          chunk_overlap: symphonySettings.chunk_overlap,
          extraction_model: symphonySettings.extraction_model,
          document_type: symphonySettings.document_type,
          system_identifier: 'symphony', // Symphony system identifier
        };

        // If a specific prompt is selected (not default), include the prompt text
        if (symphonySettings.prompt_id && symphonySettings.prompt_id !== 'default') {
          const selectedPrompt = prompts.find(p => p.id === symphonySettings.prompt_id);
          if (selectedPrompt) {
            processingConfig.prompt_text = selectedPrompt.prompt_text;
          }
        }
      } else {
        // Even if settings haven't loaded, ensure system_identifier is set
        processingConfig = {
          system_identifier: 'symphony',
        };
      }

      const uploadedDocs = await documentService.uploadDocuments(
        files,
        (progress) => {
          setUploadProgress(progress);
        },
        processingConfig
      );

      setSuccess(`Successfully uploaded ${uploadedDocs.length} document(s)`);
      setFiles([]);
      setUploadProgress([]);

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error?.message || 'Failed to upload documents');
    } finally {
      setIsUploading(false);
    }
  };

  const getProgressIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <FileIcon />;
    }
  };

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
          borderRadius: 2,
          backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <CloudUploadIcon sx={{ fontSize: 64, color: '#9e9e9e', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Drag and drop files here
          </Typography>
          <Typography variant="body2" color="textSecondary">
            or click to browse
          </Typography>
          {maxFileSize && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              Maximum file size: {documentService.formatFileSize(maxFileSize)}
            </Typography>
          )}
        </Box>
      </Paper>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileSelect}
        accept={acceptedFormats?.join(',')}
      />

      {files.length > 0 && (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Selected Files ({files.length})
          </Typography>
          <List>
            {files.map((file, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  !isUploading && (
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFile(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )
                }
              >
                <ListItemIcon>
                  <FileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${documentService.formatFileSize(file.size)} • ${file.type || 'Unknown'}`}
                  secondaryTypographyProps={{
                    component: 'div',
                    sx: { display: 'flex', gap: 1, alignItems: 'center' }
                  }}
                />
              </ListItem>
            ))}
          </List>

          {uploadProgress.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Progress
              </Typography>
              <List dense>
                {uploadProgress.map((progress, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {getProgressIcon(progress.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={progress.fileName}
                      secondary={
                        progress.status === 'uploading' ? (
                          <LinearProgress
                            variant="determinate"
                            value={progress.progress}
                            sx={{ mt: 1 }}
                          />
                        ) : progress.error ? (
                          <Typography variant="caption" color="error">
                            {progress.error}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="success.main">
                            {progress.status}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setFiles([]);
                setUploadProgress([]);
              }}
              disabled={isUploading}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUpload}
              disabled={isUploading || files.length === 0}
            >
              {isUploading ? `Uploading...` : `Upload ${files.length} File(s)`}
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentUpload;
